import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cancelReceivable,
  createReceivable,
  fetchGenerationFailures,
  fetchReceivable,
  fetchReceivables,
  registerPayment,
  retryGeneration,
  reversePayment,
  newRequestId,
  updateReceivable,
  PAYMENT_METHOD_LABELS,
  RECEIVABLE_STATUS_LABELS,
  type PaymentMethod,
  type ReceivableDetail,
  type ReceivableGenerationFailure,
  type ReceivableListItem,
  type ReceivablePayment,
  type ReceivableStatus,
} from "@/lib/adminReceivablesApi";
import { fetchAdminCustomers, fetchAdminCustomer } from "@/lib/adminCustomersApi";
import type { AdminClientSummary } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { Loader2, Plus, Search, Wallet, Pencil, Ban, HandCoins, Undo2, AlertTriangle } from "lucide-react";

const PAYMENT_METHODS: PaymentMethod[] = ["BOLETO", "PIX", "TRANSFERENCIA", "DINHEIRO"];
const STATUS_FILTERS: (ReceivableStatus | "TODOS")[] = ["TODOS", "ABERTO", "PARCIAL", "QUITADO", "CANCELADO"];

function formatBRL(value: string | number): string {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

function statusVariant(status: ReceivableStatus): "default" | "secondary" | "destructive" | "outline" {
  if (status === "CANCELADO") return "secondary";
  if (status === "QUITADO" || status === "RECEBIDO" || status === "CONCILIADO") return "default";
  if (status === "PARCIAL") return "outline";
  return "outline";
}

interface CreateDraft {
  customerId: string;
  issueDate: string;
  dueDate: string;
  paymentTermDays: string;
  paymentMethod: PaymentMethod | "";
  originalAmount: string;
  discountAmount: string;
  installmentCount: string;
  document: string;
  nfNumber: string;
  notes: string;
}

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_CREATE: CreateDraft = {
  customerId: "",
  issueDate: today(),
  dueDate: "",
  paymentTermDays: "",
  paymentMethod: "",
  originalAmount: "",
  discountAmount: "",
  installmentCount: "1",
  document: "",
  nfNumber: "",
  notes: "",
};

export default function AdminReceivables() {
  // Cada ação financeira é uma permissão separada (o backend revalida; Receber ≠ Estornar ≠ Cancelar).
  const account = useApp((s) => s.account);
  const canCreate = hasPermission(account, "FINANCEIRO.CONTAS_RECEBER.CADASTRAR");
  const canEdit = hasPermission(account, "FINANCEIRO.CONTAS_RECEBER.EDITAR");
  const canReceive = hasPermission(account, "FINANCEIRO.CONTAS_RECEBER.RECEBER");
  const canReverse = hasPermission(account, "FINANCEIRO.CONTAS_RECEBER.ESTORNAR");
  const canCancel = hasPermission(account, "FINANCEIRO.CONTAS_RECEBER.CANCELAR");
  const canReprocess = hasPermission(account, "FINANCEIRO.GERACAO.REPROCESSAR");
  const canViewGeneration = hasPermission(account, "FINANCEIRO.GERACAO.VISUALIZAR");

  const [items, setItems] = useState<ReceivableListItem[]>([]);
  const [customers, setCustomers] = useState<AdminClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ReceivableStatus | "TODOS">("TODOS");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<CreateDraft>(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [inherited, setInherited] = useState<{ term: number | null; method: PaymentMethod | null; installments: number } | null>(null);

  const [detail, setDetail] = useState<ReceivableDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const [payOpen, setPayOpen] = useState(false);

  const [failures, setFailures] = useState<ReceivableGenerationFailure[]>([]);
  const [retrying, setRetrying] = useState<string | null>(null);

  const loadFailures = useCallback(async () => {
    // Painel de falhas de geração só com FINANCEIRO.GERACAO.VISUALIZAR (evita 403 e não exibe o painel).
    if (!hasPermission(useApp.getState().account, "FINANCEIRO.GERACAO.VISUALIZAR")) return;
    try {
      setFailures(await fetchGenerationFailures());
    } catch {
      /* silencioso: seção secundária */
    }
  }, []);

  useEffect(() => { if (canViewGeneration) void loadFailures(); }, [loadFailures, canViewGeneration]);

  const handleRetry = async (orderId: string) => {
    setRetrying(orderId);
    try {
      await retryGeneration(orderId);
      toast.success("Reprocessamento solicitado");
      await Promise.all([loadFailures(), load()]);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao reprocessar"));
    } finally {
      setRetrying(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await fetchReceivables();
      setItems(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar Contas a Receber"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!canCreate) return;
    void fetchAdminCustomers()
      .then(setCustomers)
      .catch(() => { /* seletor opcional: silencioso */ });
  }, [canCreate]);

  const customerLabel = (c: AdminClientSummary) => c.companyName || c.email;

  const openCreate = () => {
    setDraft({ ...EMPTY_CREATE, issueDate: today() });
    setInherited(null);
    setCreateOpen(true);
  };

  // O CustomerAccount é a fonte da condição financeira: ao escolher o cliente, herdamos prazo/forma/
  // parcelas e pré-preenchemos o formulário. O admin só digita se for uma exceção manual explícita.
  const onSelectCustomer = async (id: string) => {
    setDraft((d) => ({ ...d, customerId: id }));
    if (!id) { setInherited(null); return; }
    try {
      const c = await fetchAdminCustomer(id);
      const term = c.defaultPaymentTermDays ?? null;
      const method = (c.defaultPaymentMethod ?? null) as PaymentMethod | null;
      const installments = c.defaultInstallments ?? 1;
      setInherited({ term, method, installments });
      setDraft((d) => ({
        ...d,
        paymentTermDays: term != null ? String(term) : d.paymentTermDays,
        paymentMethod: method ?? d.paymentMethod,
        installmentCount: String(installments),
      }));
    } catch {
      setInherited(null);
    }
  };

  const handleCreate = async () => {
    if (!draft.customerId) { toast.error("Selecione o cliente"); return; }
    if (!draft.paymentMethod) { toast.error("Selecione a forma de pagamento"); return; }
    if (!draft.originalAmount || Number(draft.originalAmount) <= 0) { toast.error("Informe o valor original"); return; }
    if (!draft.dueDate && !draft.paymentTermDays) {
      toast.error("Informe a data de vencimento ou o prazo em dias");
      return;
    }
    setSaving(true);
    try {
      await createReceivable({
        customerId: draft.customerId,
        issueDate: draft.issueDate,
        dueDate: draft.dueDate || undefined,
        paymentTermDays: draft.paymentTermDays ? Number(draft.paymentTermDays) : undefined,
        paymentMethod: draft.paymentMethod as PaymentMethod,
        originalAmount: Number(draft.originalAmount),
        discountAmount: draft.discountAmount ? Number(draft.discountAmount) : undefined,
        installmentCount: draft.installmentCount ? Number(draft.installmentCount) : undefined,
        document: draft.document.trim() || undefined,
        nfNumber: draft.nfNumber.trim() || undefined,
        notes: draft.notes.trim() || undefined,
      });
      toast.success("Título lançado");
      setCreateOpen(false);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao lançar título"));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetail(null);
    setEditing(false);
    try {
      setDetail(await fetchReceivable(id));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar título"));
      setDetailOpen(false);
    }
  };

  const handleUpdate = async (patch: Parameters<typeof updateReceivable>[1]) => {
    if (!detail) return;
    setSaving(true);
    try {
      const updated = await updateReceivable(detail.id, patch);
      setDetail(updated);
      setEditing(false);
      toast.success("Título atualizado");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao atualizar título"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!detail) return;
    const reason = window.prompt("Motivo do cancelamento do título:");
    if (!reason || !reason.trim()) return;
    setSaving(true);
    try {
      const updated = await cancelReceivable(detail.id, reason.trim());
      setDetail(updated);
      toast.success("Título cancelado");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao cancelar título"));
    } finally {
      setSaving(false);
    }
  };

  const onPaymentRegistered = async (updated: ReceivableDetail) => {
    setDetail(updated);
    setPayOpen(false);
    await load();
  };

  const handleReverse = async (payment: ReceivablePayment) => {
    if (!detail) return;
    const reason = window.prompt(`Motivo do estorno do recebimento de ${formatBRL(payment.amount)}:`);
    if (!reason || !reason.trim()) return;
    setSaving(true);
    try {
      const updated = await reversePayment(payment.id, reason.trim());
      setDetail(updated);
      toast.success("Recebimento estornado");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao estornar recebimento"));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== "TODOS" && it.status !== statusFilter) return false;
      if (overdueOnly && !it.overdue) return false;
      if (!q) return true;
      return (
        String(it.number).includes(q) ||
        it.customerName.toLowerCase().includes(q) ||
        (it.document ?? "").toLowerCase().includes(q)
      );
    });
  }, [items, search, statusFilter, overdueOnly]);

  const totals = useMemo(() => {
    const open = items.filter((i) => i.status === "ABERTO" || i.status === "PARCIAL");
    const overdue = items.filter((i) => i.overdue);
    const sumBalance = (list: ReceivableListItem[]) => list.reduce((acc, i) => acc + Number(i.balance), 0);
    return {
      openCount: open.length,
      openSum: sumBalance(open),
      overdueCount: overdue.length,
      overdueSum: sumBalance(overdue),
    };
  }, [items]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Contas a Receber</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Títulos financeiros, recebimentos e estornos — todos auditados.
            </p>
          </div>
          {canCreate && (
            <Button variant="secondary" onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" /> Novo título
            </Button>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-lg bg-white/10 p-3">
            <div className="text-xs uppercase text-primary-foreground/70">Saldo em aberto</div>
            <div className="text-lg font-bold">{formatBRL(totals.openSum)}</div>
            <div className="text-xs text-primary-foreground/70">{totals.openCount} títulos</div>
          </div>
          <div className="rounded-lg bg-white/10 p-3">
            <div className="text-xs uppercase text-primary-foreground/70">Atrasado</div>
            <div className="text-lg font-bold">{formatBRL(totals.overdueSum)}</div>
            <div className="text-xs text-primary-foreground/70">{totals.overdueCount} títulos</div>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Search className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Buscar por nº, cliente ou documento"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="rounded-lg bg-white/10 px-3 py-2 text-sm text-primary-foreground outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReceivableStatus | "TODOS")}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s} className="text-foreground">
                {s === "TODOS" ? "Todas as situações" : RECEIVABLE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
            Somente atrasados
          </label>
        </div>
      </div>

      {canViewGeneration && failures.length > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <div className="mb-2 text-sm font-semibold text-destructive">
            Pendências de geração ({failures.length})
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Expedição</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-center">Tentativas</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((f) => (
                  <TableRow key={f.orderId}>
                    <TableCell className="font-medium">{f.orderNumber ? `#${f.orderNumber}` : "—"}</TableCell>
                    <TableCell>{f.customerName ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {f.expeditionDate ? formatDate(f.expeditionDate) : "—"}
                    </TableCell>
                    <TableCell className="max-w-xs text-xs text-muted-foreground">{f.reason ?? "—"}</TableCell>
                    <TableCell className="text-center">{f.attempts}</TableCell>
                    <TableCell className="text-right">
                      {canReprocess && (
                        <Button size="sm" variant="outline" disabled={retrying === f.orderId}
                          onClick={() => void handleRetry(f.orderId)}>
                          {retrying === f.orderId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Reprocessar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Wallet className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nenhum título encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nº</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Recebido</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Situação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} className="cursor-pointer" onClick={() => void openDetail(it.id)}>
                  <TableCell className="font-medium">#{it.number}</TableCell>
                  <TableCell>
                    <div className="font-medium">{it.customerName}</div>
                    {it.document && <div className="text-xs text-muted-foreground">{it.document}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(it.dueDate)}</TableCell>
                  <TableCell className="text-right font-medium">{formatBRL(it.netAmount)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatBRL(it.receivedAmount)}</TableCell>
                  <TableCell className="text-right font-medium">{formatBRL(it.balance)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={statusVariant(it.status)}>{RECEIVABLE_STATUS_LABELS[it.status]}</Badge>
                      {it.overdue && <Badge variant="destructive">Atrasado</Badge>}
                      {it.orderCancelled && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">Pedido cancelado</Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) setCreateOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo título a receber</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="rc-customer">Cliente *</Label>
              <select
                id="rc-customer"
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={draft.customerId}
                onChange={(e) => void onSelectCustomer(e.target.value)}
              >
                <option value="">Selecione…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{customerLabel(c)}</option>
                ))}
              </select>
              {inherited && (inherited.method || inherited.term != null) ? (
                <p className="text-xs text-muted-foreground">
                  Condição herdada do cliente (congelada no título):{" "}
                  {inherited.method ? PAYMENT_METHOD_LABELS[inherited.method] : "sem forma"}
                  {inherited.term != null ? `, ${inherited.term} dias` : ""}
                  {`, ${inherited.installments}x`}. Edite abaixo apenas para uma exceção.
                </p>
              ) : inherited ? (
                <p className="text-xs text-amber-600">
                  Cliente sem condição financeira padrão — informe forma e prazo/vencimento abaixo.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-issue">Emissão *</Label>
              <Input id="rc-issue" type="date" value={draft.issueDate}
                onChange={(e) => setDraft((d) => ({ ...d, issueDate: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-due">Vencimento</Label>
              <Input id="rc-due" type="date" value={draft.dueDate}
                onChange={(e) => setDraft((d) => ({ ...d, dueDate: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-term">Prazo (dias)</Label>
              <Input id="rc-term" type="number" min={0} placeholder="ex.: 28" value={draft.paymentTermDays}
                onChange={(e) => setDraft((d) => ({ ...d, paymentTermDays: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-method">Forma de pagamento *</Label>
              <select
                id="rc-method"
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={draft.paymentMethod}
                onChange={(e) => setDraft((d) => ({ ...d, paymentMethod: e.target.value as PaymentMethod }))}
              >
                <option value="">Selecione…</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-original">Valor original *</Label>
              <Input id="rc-original" type="number" min={0} step="0.01" value={draft.originalAmount}
                onChange={(e) => setDraft((d) => ({ ...d, originalAmount: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-discount">Desconto</Label>
              <Input id="rc-discount" type="number" min={0} step="0.01" value={draft.discountAmount}
                onChange={(e) => setDraft((d) => ({ ...d, discountAmount: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-installments">Parcelas</Label>
              <Input id="rc-installments" type="number" min={1} value={draft.installmentCount}
                onChange={(e) => setDraft((d) => ({ ...d, installmentCount: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-doc">Documento</Label>
              <Input id="rc-doc" value={draft.document}
                onChange={(e) => setDraft((d) => ({ ...d, document: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rc-nf">NF</Label>
              <Input id="rc-nf" value={draft.nfNumber}
                onChange={(e) => setDraft((d) => ({ ...d, nfNumber: e.target.value }))} />
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="rc-notes">Observações</Label>
              <Textarea id="rc-notes" rows={2} value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={() => void handleCreate()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Lançar título
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={(o) => { if (!o) { setDetailOpen(false); setEditing(false); } }}>
        <DialogContent className="max-w-2xl">
          {!detail ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  Título #{detail.number}
                  <Badge variant={statusVariant(detail.status)}>{RECEIVABLE_STATUS_LABELS[detail.status]}</Badge>
                  {detail.overdue && <Badge variant="destructive">Atrasado</Badge>}
                </DialogTitle>
              </DialogHeader>

              {detail.orderCancelled && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span><strong>Pedido de origem cancelado — revisão financeira necessária.</strong> O título
                    manteve recebimento válido e não foi cancelado automaticamente.</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-muted-foreground">Cliente:</span> {detail.customerName}</div>
                <div><span className="text-muted-foreground">Origem:</span> {detail.origin}</div>
                <div><span className="text-muted-foreground">Emissão:</span> {formatDate(detail.issueDate)}</div>
                <div><span className="text-muted-foreground">Vencimento:</span> {formatDate(detail.dueDate)}</div>
                <div><span className="text-muted-foreground">Original:</span> {formatBRL(detail.originalAmount)}</div>
                <div><span className="text-muted-foreground">Desconto:</span> {formatBRL(detail.discountAmount)}</div>
                <div><span className="text-muted-foreground">Líquido:</span> <strong>{formatBRL(detail.netAmount)}</strong></div>
                <div><span className="text-muted-foreground">Forma (condição):</span> {PAYMENT_METHOD_LABELS[detail.paymentMethod]}</div>
                <div><span className="text-muted-foreground">Recebido:</span> {formatBRL(detail.receivedAmount)}</div>
                <div><span className="text-muted-foreground">Saldo:</span> <strong>{formatBRL(detail.balance)}</strong></div>
                <div><span className="text-muted-foreground">Parcela:</span> {detail.installmentNumber}/{detail.installmentCount}</div>
                <div><span className="text-muted-foreground">NF:</span> {detail.nfNumber?.trim() ? detail.nfNumber : "Não informada"}</div>
              </div>
              {detail.cancelledAt && (
                <div className="rounded-md bg-muted p-3 text-sm">
                  <strong>Cancelado.</strong> Motivo: {detail.cancelledReason ?? "—"}
                </div>
              )}

              {/* Recebimentos */}
              <div className="mt-2">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-semibold">Recebimentos</div>
                  {canReceive && !detail.cancelledAt && Number(detail.balance) > 0 && (
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => setPayOpen(true)}>
                      <HandCoins className="h-4 w-4" /> Registrar recebimento
                    </Button>
                  )}
                </div>
                {detail.payments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum recebimento registrado.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {detail.payments.map((p) => (
                      <div key={p.id} className={`rounded-md border p-2 text-xs ${p.reversed ? "opacity-60" : ""}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-medium">
                            {formatBRL(p.amount)} · {formatDate(p.paidAt)} · {PAYMENT_METHOD_LABELS[p.paymentMethod]}
                            {p.reversed && <span className="ml-2 text-destructive">(estornado)</span>}
                          </div>
                          {canReverse && !p.reversed && !detail.cancelledAt && (
                            <Button size="sm" variant="ghost" className="h-7 gap-1 text-destructive hover:text-destructive"
                              disabled={saving} onClick={() => void handleReverse(p)}>
                              <Undo2 className="h-3.5 w-3.5" /> Estornar
                            </Button>
                          )}
                        </div>
                        {p.externalReference && <div className="text-muted-foreground">Ref.: {p.externalReference}</div>}
                        {p.notes && <div className="text-muted-foreground">{p.notes}</div>}
                        <div className="text-muted-foreground">
                          {p.createdByEmail ?? "sistema"} · {new Date(p.createdAt).toLocaleString("pt-BR")}
                        </div>
                        {p.reversed && (
                          <div className="mt-1 rounded bg-muted p-1 text-muted-foreground">
                            Estornado por {p.reversedByEmail ?? "—"}
                            {p.reversedAt ? ` em ${new Date(p.reversedAt).toLocaleString("pt-BR")}` : ""}.
                            {p.reversedReason ? ` Motivo: ${p.reversedReason}` : ""}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-2">
                <div className="mb-2 text-sm font-semibold">Histórico</div>
                <div className="flex max-h-56 flex-col gap-2 overflow-y-auto">
                  {detail.events.map((e) => (
                    <div key={e.id} className="rounded-md border p-2 text-xs">
                      <div className="font-medium">{e.title}</div>
                      {e.field && (
                        <div className="text-muted-foreground">{e.field}: {e.oldValue ?? "—"} → {e.newValue ?? "—"}</div>
                      )}
                      {e.details && <div className="text-muted-foreground">{e.details}</div>}
                      <div className="text-muted-foreground">
                        {e.actorEmail ?? "sistema"} · {new Date(e.at).toLocaleString("pt-BR")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(canEdit || canCancel) && !detail.cancelledAt && (
                <DialogFooter className="gap-2">
                  {canEdit && (
                    <Button variant="outline" onClick={() => setEditing((v) => !v)} disabled={saving} className="gap-2">
                      <Pencil className="h-4 w-4" /> {editing ? "Fechar edição" : "Editar"}
                    </Button>
                  )}
                  {canCancel && (
                    <Button variant="ghost" className="gap-2 text-destructive hover:text-destructive"
                      onClick={() => void handleCancel()} disabled={saving}>
                      <Ban className="h-4 w-4" /> Cancelar título
                    </Button>
                  )}
                </DialogFooter>
              )}

              {editing && canEdit && !detail.cancelledAt && (
                <EditForm detail={detail} saving={saving} onSubmit={handleUpdate} />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Register payment dialog */}
      {detail && (
        <PaymentDialog
          open={payOpen}
          detail={detail}
          onClose={() => setPayOpen(false)}
          onDone={onPaymentRegistered}
        />
      )}
    </div>
  );
}

function PaymentDialog({
  open,
  detail,
  onClose,
  onDone,
}: {
  open: boolean;
  detail: ReceivableDetail;
  onClose: () => void;
  onDone: (updated: ReceivableDetail) => void;
}) {
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState<PaymentMethod>(detail.paymentMethod);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  // Chave de idempotência por operação: gerada ao abrir e reutilizada em reenvios da mesma operação.
  const [requestId, setRequestId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(detail.balance);
      setPaidAt(today());
      setMethod(detail.paymentMethod);
      setReference("");
      setNotes("");
      setRequestId(newRequestId());
    }
  }, [open, detail.balance, detail.paymentMethod]);

  const submit = async () => {
    const value = Number(amount);
    if (!value || value <= 0) { toast.error("Informe um valor positivo"); return; }
    if (value > Number(detail.balance)) { toast.error("O valor excede o saldo em aberto"); return; }
    setSaving(true);
    try {
      const updated = await registerPayment(detail.id, {
        amount: value,
        paidAt,
        paymentMethod: method,
        externalReference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        requestId, // reutilizado em reenvios da mesma operação (idempotência)
      });
      toast.success("Recebimento registrado");
      onDone(updated);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao registrar recebimento"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar recebimento — título #{detail.number}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 rounded-md bg-muted p-2 text-xs text-muted-foreground">
            Saldo em aberto: <strong>{formatBRL(detail.balance)}</strong> · Líquido {formatBRL(detail.netAmount)}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-amount">Valor recebido *</Label>
            <Input id="pay-amount" type="number" min={0} step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-date">Data do recebimento *</Label>
            <Input id="pay-date" type="date" max={today()} value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-method">Forma efetiva *</Label>
            <select id="pay-method" className="h-9 rounded-md border bg-background px-3 text-sm"
              value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pay-ref">Referência</Label>
            <Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="pay-notes">Observação</Label>
            <Textarea id="pay-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditForm({
  detail,
  saving,
  onSubmit,
}: {
  detail: ReceivableDetail;
  saving: boolean;
  onSubmit: (patch: Parameters<typeof updateReceivable>[1]) => void;
}) {
  const [dueDate, setDueDate] = useState(detail.dueDate);
  const [originalAmount, setOriginalAmount] = useState(detail.originalAmount);
  const [discountAmount, setDiscountAmount] = useState(detail.discountAmount);
  const [nfNumber, setNfNumber] = useState(detail.nfNumber ?? "");
  const [notes, setNotes] = useState(detail.notes ?? "");

  // Correção #6: após recebimento registrado (mesmo estornado), os valores financeiros ficam travados.
  const hasPayments = detail.payments.length > 0;

  return (
    <div className="mt-2 grid grid-cols-2 gap-4 border-t pt-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ed-due">Vencimento</Label>
        <Input id="ed-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ed-nf">NF</Label>
        <Input id="ed-nf" value={nfNumber} onChange={(e) => setNfNumber(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ed-original">Valor original</Label>
        <Input id="ed-original" type="number" min={0} step="0.01" value={originalAmount}
          disabled={hasPayments} onChange={(e) => setOriginalAmount(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ed-discount">Desconto</Label>
        <Input id="ed-discount" type="number" min={0} step="0.01" value={discountAmount}
          disabled={hasPayments} onChange={(e) => setDiscountAmount(e.target.value)} />
      </div>
      {hasPayments && (
        <p className="col-span-2 text-xs text-amber-600">
          Título com recebimento registrado — os valores financeiros não podem ser alterados. Estorne os
          recebimentos para ajustar o título.
        </p>
      )}
      <div className="col-span-2 flex flex-col gap-1.5">
        <Label htmlFor="ed-notes">Observações</Label>
        <Textarea id="ed-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="col-span-2 flex justify-end">
        <Button
          disabled={saving}
          onClick={() =>
            onSubmit({
              dueDate,
              ...(hasPayments ? {} : {
                originalAmount: Number(originalAmount),
                discountAmount: Number(discountAmount),
              }),
              nfNumber: nfNumber.trim(),
              notes: notes.trim(),
            })
          }
        >
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar alterações
        </Button>
      </div>
    </div>
  );
}
